<template>
  <aside class="reg-sidebar">
    <div class="reg-sidebar__brand">
      <img :src="brand.logos.panel" class="reg-sidebar__logo" alt="" />
      <span class="reg-sidebar__brand-name">{{ brand.shortName }}</span>
    </div>

    <div class="reg-sidebar__intro">
      <h1 class="reg-sidebar__title">{{ $t('registration.sidebar.title') }}</h1>
      <p class="reg-sidebar__subtitle">{{ $t('registration.sidebar.subtitle') }}</p>
    </div>

    <!-- only passed steps are clickable -->
    <ol class="reg-sidebar__steps">
      <li v-for="step in steps" :key="step.key">
        <button
          v-if="step.isClickable"
          type="button"
          class="reg-sidebar__step reg-sidebar__step--done reg-sidebar__step--clickable"
          :data-testid="`reg-sidebar-step-${step.key}`"
          @click="emit('stepClick', step.sequence)">
          <span class="reg-sidebar__marker">
            <IconCheck :size="14" />
          </span>
          <span class="reg-sidebar__label">{{ step.label }}</span>
        </button>
        <span
          v-else
          class="reg-sidebar__step"
          :class="{
            'reg-sidebar__step--current': step.isCurrent,
            'reg-sidebar__step--done': step.isDone,
          }"
          :aria-current="step.isCurrent ? 'step' : undefined"
          :data-testid="`reg-sidebar-step-${step.key}`">
          <span class="reg-sidebar__marker">
            <IconCheck v-if="step.isDone" :size="14" />
            <template v-else>{{ step.sequence }}</template>
          </span>
          <span class="reg-sidebar__label">{{ step.label }}</span>
          <span v-if="step.isCurrent" class="reg-sidebar__meta">{{ $t('registration.sidebar.now') }}</span>
        </span>
      </li>
    </ol>

    <div class="reg-sidebar__support" data-testid="reg-sidebar-support">
      <span class="reg-sidebar__initials">SB</span>
      <span class="reg-sidebar__support-text">
        <span class="reg-sidebar__support-name">{{ $t('registration.support.name') }}</span>
        <a class="reg-sidebar__support-phone" :href="`tel:${SUPPORT_PHONE_HREF}`">{{ SUPPORT_PHONE }}</a>
      </span>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { brand } from '@/config/brands';
import { IconCheck } from '@tabler/icons-vue';

import { SUPPORT_PHONE, SUPPORT_PHONE_HREF } from '@/components/registration/subcomponents/support';

interface SidebarStep {
  key: string;
  sequence: number;
  label: string;
  isCurrent: boolean;
  isDone: boolean;
  isClickable: boolean;
}

interface Props {
  steps: SidebarStep[];
}

defineProps<Props>();

const emit = defineEmits<{ stepClick: [sequence: number] }>();
</script>

<style scoped lang="scss">
@use '@/assets/registration' as reg;

.reg-sidebar {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: reg.$sidebar-width;
  flex: none;
  // bottom padding matches the footer's so the support divider lines up
  padding: 40px 32px reg.$footer-padding-y;
  background: reg.$sidebar-bg;
  font-family:
    var(--font-brand),
    -apple-system,
    BlinkMacSystemFont,
    'San Francisco',
    'Segoe UI',
    Roboto,
    'Helvetica Neue',
    sans-serif;
  overflow-y: auto;

  &__brand {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__logo {
    width: 40px;
    height: 40px;
    object-fit: contain;
    flex: none;
  }

  &__brand-name {
    font-size: 20px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: #fff;
  }

  &__intro {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__title {
    margin: 0;
    font-size: 20px;
    line-height: 28px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #fff;
    text-wrap: pretty;
  }

  &__subtitle {
    margin: 0;
    font-size: 13px;
    line-height: 20px;
    color: reg.$sidebar-fg;
    text-wrap: pretty;
  }

  &__steps {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    padding: 0;
    list-style: none;
    flex: 1;
  }

  &__step {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 9px 12px;
    border-radius: 6px;
    color: reg.$sidebar-muted;
    font-size: 13px;
    line-height: 20px;
    animation: reg-step-in 0.45s cubic-bezier(0.2, 0.7, 0.3, 1) both;

    &--done {
      color: reg.$sidebar-fg;
    }

    &--current {
      padding: 11px 12px;
      background: reg.$sidebar-surface;
      color: #fff;
      font-weight: 600;
    }

    // reset button chrome
    &--clickable {
      background: none;
      border: none;
      text-align: left;
      font-family: inherit;
      cursor: pointer;

      &:hover {
        background: reg.$sidebar-surface-subtle;
      }

      &:focus-visible {
        outline: 2px solid reg.$sidebar-link;
        outline-offset: -2px;
      }
    }
  }

  &__marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    flex: none;
    border-radius: 9999px;
    border: 1px solid reg.$sidebar-ring;
    font-size: 13px;
    line-height: 1;
    color: reg.$sidebar-muted;
  }

  &__step--current &__marker,
  &__step--done &__marker {
    border-color: transparent;
    background: var(--tw-color-primary);
    color: #fff;
  }

  &__label {
    flex: 1;
  }

  &__meta {
    margin-left: auto;
    font-size: 12px;
    line-height: 1;
    color: reg.$sidebar-muted;
  }

  &__support {
    display: flex;
    align-items: center;
    gap: 12px;
    // top padding matches the footer's so the rule lines up with its border
    padding-top: reg.$footer-padding-y;
    border-top: 1px solid reg.$sidebar-border;
  }

  &__initials {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex: none;
    border-radius: 9999px;
    background: reg.$sidebar-surface;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
  }

  &__support-text {
    display: flex;
    flex-direction: column;
  }

  &__support-name {
    font-size: 13px;
    line-height: 20px;
    color: reg.$sidebar-fg;
  }

  &__support-phone {
    font-size: 13px;
    line-height: 20px;
    font-weight: 500;
    color: reg.$sidebar-link;
  }
}

@keyframes reg-step-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (min-width: 768px) and (max-width: 1199.98px) {
  .reg-sidebar {
    width: reg.$sidebar-width-tablet;
    padding: 32px 20px reg.$footer-padding-y-tablet;
    gap: 24px;
  }

  .reg-sidebar__support {
    padding-top: reg.$footer-padding-y-tablet;
  }
}
</style>
